import React from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';

export interface ToolHeaderProps {
  /** Title of the tool */
  title: string;
  /** Optional subtitle or context badge, e.g. track name or tyre description */
  subtitle?: string;
  /** Optional icon component displayed alongside the title */
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  /** Custom click handler for exit / back button */
  onExit?: () => void;
  /** Custom click handler for close button */
  onClose?: () => void;
  /** Custom route to navigate to on exit */
  exitHref?: string;
  /** Custom route to navigate to on close */
  closeHref?: string;
  /** Custom text for exit / back button (defaults to Exit) */
  exitLabel?: string;
  /** Custom text for close button (defaults to Close) */
  closeLabel?: string;
  /** Extra custom actions to display */
  actions?: React.ReactNode;
  /** Optional container class name override (defaults to 'mb-6') */
  className?: string;
}

export function ToolHeader({
  title,
  subtitle,
  icon: Icon,
  onExit,
  onClose,
  exitHref,
  closeHref,
  exitLabel,
  closeLabel,
  actions,
  className = 'mb-6',
}: ToolHeaderProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const handleExit = () => {
    if (onExit) {
      onExit();
    } else if (exitHref) {
      navigate(exitHref);
    } else {
      navigate('#/');
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else if (closeHref) {
      navigate(closeHref);
    } else {
      navigate('#/');
    }
  };

  return (
    <div className={`relative flex items-center justify-between gap-4 py-1 ${className}`}>
      {/* Exit / Back button */}
      <button
        onClick={handleExit}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer z-10 flex-shrink-0"
        title={exitLabel ?? t('common.exit')}
      >
        <ArrowLeft size={14} />
        <span>{exitLabel ?? t('common.exit')}</span>
      </button>

      {/* Center title area */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-16 sm:px-24">
        <div className="flex items-center justify-center gap-2 max-w-full">
          {Icon && (
            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-accent-subtle flex-shrink-0">
              <Icon size={14} className="text-accent" />
            </div>
          )}
          <h1 className="text-sm sm:text-base font-bold tracking-tight text-text-primary truncate">
            {title}
          </h1>
        </div>
        {subtitle && (
          <div className="mt-0.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-medium bg-bg-surface/80 border border-border-subtle text-text-secondary truncate max-w-xs sm:max-w-md">
              {subtitle}
            </span>
          </div>
        )}
      </div>

      {/* Right-side actions & Close button */}
      <div className="flex items-center gap-3 z-10 ml-auto flex-shrink-0">
        {actions}
        <button
          onClick={handleClose}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
          title={closeLabel ?? t('common.close')}
        >
          <X size={14} />
          <span>{closeLabel ?? t('common.close')}</span>
        </button>
      </div>
    </div>
  );
}

